import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import logo from '../assets/logo.png';
import './Navigation.css';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLanguageChange = (newLanguage: 'en' | 'pt' | 'es') => {
    setLanguage(newLanguage);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = () => {
    closeMenu();
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        {/* Left Section - Navigation Links */}
        <div className="nav-left">
          <ul className="nav-menu">
            <li className="nav-item">
              <Link
                to="/"
                className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                {t('nav.home')}
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/gallery"
                className={`nav-link ${location.pathname === '/gallery' ? 'active' : ''}`}
              >
                {t('nav.gallery')}
              </Link>
            </li>
            <li className="nav-item">
              <Link
                to="/contact"
                className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
              >
                {t('nav.contact')}
              </Link>
            </li>
          </ul>
        </div>

        {/* Center Section - Logo */}
        <Link to="/" className="logo">
          <img src={logo} alt="Cabanna's Party Rentals" className="logo-image" />
        </Link>

        {/* Right Section - Phone & Language */}
        <div className="nav-right">
          {/* Phone Number */}
          <div className="nav-phone">
            <a href="tel:+14073982139" className="phone-link">
              📞 (407) 398-2139
            </a>
          </div>

          {/* Language Switcher */}
          <div className="language-switcher">
            <button
              className={`lang-button ${language === 'en' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
              title="English"
            >
              🇺🇸 EN
            </button>
            <button
              className={`lang-button ${language === 'es' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('es')}
              title="Español"
            >
              🇪🇸 ES
            </button>
            <button
              className={`lang-button ${language === 'pt' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('pt')}
              title="Português"
            >
              🇧🇷 PT
            </button>
          </div>
        </div>

        {/* Hamburger Menu Button */}
        <button className="hamburger-menu" onClick={toggleMenu} aria-label="Toggle menu">
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>

        {/* Mobile Menu */}
        <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
          <ul className="mobile-nav-menu">
            <li className="mobile-nav-item">
              <Link
                to="/"
                className={`mobile-nav-link ${location.pathname === '/' ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {t('nav.home')}
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/gallery"
                className={`mobile-nav-link ${location.pathname === '/gallery' ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {t('nav.gallery')}
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/contact"
                className={`mobile-nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                {t('nav.contact')}
              </Link>
            </li>
          </ul>

          {/* Mobile Contact Links */}
          <div className="mobile-contact-links">
            <a href="tel:+14073982139" className="mobile-contact-link phone">
              📞 (407) 398-2139
            </a>
            <a
              href={`https://wa.me/14073982139?text=${encodeURIComponent(t('contact.whatsapp.message'))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-contact-link whatsapp"
            >
              <svg width="1.2rem" height="1.2rem" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '8px'}}>
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.085"/>
              </svg>
              WhatsApp
            </a>
          </div>

          {/* Mobile Language Switcher */}
          <div className="mobile-language-switcher">
            <button
              className={`mobile-lang-button ${language === 'en' ? 'active' : ''}`}
              onClick={() => { handleLanguageChange('en'); closeMenu(); }}
              title="English"
            >
              🇺🇸 EN
            </button>
            <button
              className={`mobile-lang-button ${language === 'es' ? 'active' : ''}`}
              onClick={() => { handleLanguageChange('es'); closeMenu(); }}
              title="Español"
            >
              🇪🇸 ES
            </button>
            <button
              className={`mobile-lang-button ${language === 'pt' ? 'active' : ''}`}
              onClick={() => { handleLanguageChange('pt'); closeMenu(); }}
              title="Português"
            >
              🇧🇷 PT
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
